import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Users, CreditCard, ScanLine, TrendingUp, AlertTriangle, DollarSign,
  Loader2, ArrowRight, Trash2, Plus, X, Zap, Percent, Activity, Camera,
} from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import {
  apiAdminListAffiliates, apiAdminCreateAffiliate, apiAdminArchiveAffiliate,
  apiAdminAnalytics, apiAdminAffiliateDetail,
} from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Card({ label, value, icon: Icon, hint, tone = "default", testid }) {
  const toneClass = {
    default: "text-[#0F172A]",
    good: "text-[#047857]",
    warn: "text-[#B45309]",
    bad: "text-[#B91C1C]",
  }[tone];
  return (
    <div className="p-5 rounded-2xl bg-white border border-[#E9ECEF]" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#64748B] font-medium">
        {Icon && <Icon size={12} />} {label}
      </div>
      <div className={`mt-1 text-2xl font-semibold font-mono-num ${toneClass}`}>{value}</div>
      {hint && <div className="mt-1 text-[11px] text-[#64748B]">{hint}</div>}
    </div>
  );
}

function Section({ title, subtitle, children, testid }) {
  return (
    <div className="p-5 rounded-2xl bg-white border border-[#E9ECEF] mt-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-lg font-semibold text-[#0F172A]">{title}</h2>
          {subtitle && <p className="text-xs text-[#64748B] mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function AlertsBanner({ alerts }) {
  if (!alerts?.length) return null;
  return (
    <div className="mt-4 space-y-2" data-testid="admin-alerts">
      {alerts.map((a, i) => (
        <div
          key={i}
          className="flex items-start gap-3 p-3 rounded-xl bg-[#FEF3C7] border border-[#F59E0B]/40"
          data-testid={`alert-${a.kind}`}
        >
          <AlertTriangle size={16} className="text-[#B45309] mt-0.5 flex-shrink-0" />
          <div className="text-[13px] text-[#78350F] leading-relaxed flex-1">
            <span className="font-semibold">{a.message}</span>
            {a.kind === "heavy_user" && a.details && (
              <ul className="mt-1 text-[11px] text-[#78350F] font-mono-num list-disc list-inside">
                {a.details.slice(0, 5).map((h) => (
                  <li key={h.session_id}>{h.session_id} — {h.scans} scans</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function AffiliateDrawer({ code, onClose }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!code) return;
    setRows(null);
    apiAdminAffiliateDetail(code)
      .then((r) => setRows(r.referrals || []))
      .catch((e) => setErr(e?.response?.data?.detail || "load_failed"));
  }, [code]);
  if (!code) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-stretch justify-end bg-black/40" onClick={onClose} data-testid="affiliate-drawer">
      <div className="w-full max-w-2xl bg-white border-l border-[#E9ECEF] p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#64748B]">Affiliate detail</div>
            <h2 className="text-xl font-semibold text-[#0F172A] font-mono-num">{code}</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F1F5F9] flex items-center justify-center" data-testid="aff-drawer-close">
            <X size={16} className="text-[#64748B]" />
          </button>
        </div>
        {err && <div className="text-sm text-[#B91C1C] py-4">{err}</div>}
        {!rows && !err && <div className="text-sm text-[#64748B] py-4 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> loading referrals…</div>}
        {rows?.length === 0 && <div className="text-sm text-[#64748B] py-4">no referrals yet for this code</div>}
        {rows?.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-[#64748B]">
                <tr>
                  <th className="text-left py-2 pr-2">Buyer</th>
                  <th className="text-left py-2 pr-2">Tier</th>
                  <th className="text-right py-2 pr-2">Amount</th>
                  <th className="text-left py-2 pr-2">Status</th>
                  <th className="text-right py-2 pr-2">Bonus</th>
                  <th className="text-left py-2">Still paying</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.payment_id} className="border-t border-[#E9ECEF]">
                    <td className="py-2 pr-2 text-[#475569] font-mono-num text-xs">{r.buyer_email || r.app_session_id}</td>
                    <td className="py-2 pr-2 text-[#0F172A]">{r.tier}</td>
                    <td className="py-2 pr-2 font-mono-num text-right text-[#0F172A]">${(r.amount_paid || 0).toFixed?.(2) ?? r.amount_paid}</td>
                    <td className="py-2 pr-2 text-[#475569]">{r.affiliate_status}</td>
                    <td className="py-2 pr-2 font-mono-num text-right text-[#047857]">+{r.bonus_credits}</td>
                    <td className="py-2 text-[#0F172A]">{r.still_subscribed ? "✓" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function AffiliatePanel({ affiliates, onRefresh }) {
  const [form, setForm] = useState({ code: "", name: "", email: "", platform: "", handle: "", rev_share_pct: 30, notes: "" });
  const [busy, setBusy] = useState(false);
  const [busyRow, setBusyRow] = useState(null);
  const [drawerCode, setDrawerCode] = useState(null);

  const create = async () => {
    const code = (form.code || "").trim().toUpperCase();
    if (!/^[A-Z0-9_-]{2,40}$/.test(code)) {
      toast.error("Code must be 2–40 chars: A–Z, 0–9, _ or -");
      return;
    }
    setBusy(true);
    try {
      await apiAdminCreateAffiliate({
        code,
        name: form.name.trim(),
        email: form.email.trim(),
        platform: form.platform.trim(),
        handle: form.handle.trim(),
        rev_share_pct: Number(form.rev_share_pct) || 30,
        notes: form.notes.trim(),
      });
      toast.success(`Created ${code}`);
      setForm({ code: "", name: "", email: "", platform: "", handle: "", rev_share_pct: 30, notes: "" });
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  const archive = async (code) => {
    if (!window.confirm(`Archive ${code}? Existing subscribers keep bonus scans; the code stops working for new checkouts.`)) return;
    setBusyRow(code);
    try {
      await apiAdminArchiveAffiliate(code);
      toast.success(`Archived ${code}`);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed");
    } finally {
      setBusyRow(null);
    }
  };

  const link = (code) => `${window.location.origin}/?ref=${encodeURIComponent(code)}`;
  const partnerLink = (code) => `${window.location.origin}/partners/${encodeURIComponent(code)}`;
  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <Section title="Affiliate codes" subtitle="Whop-native · runtime creatable · default 30% rev share · click a row to see referrals" testid="admin-affiliates-panel">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2 mb-4" data-testid="admin-affiliates-create">
        <input data-testid="aff-input-code" placeholder="CODE" value={form.code}
          onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, "").slice(0, 40) })}
          className="px-3 py-2 rounded-lg border border-[#E9ECEF] bg-white text-sm font-mono-num uppercase text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0F172A]" />
        <input data-testid="aff-input-name" placeholder="Creator name" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="px-3 py-2 rounded-lg border border-[#E9ECEF] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0F172A]" />
        <input data-testid="aff-input-email" placeholder="Email (payouts)" type="email" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="px-3 py-2 rounded-lg border border-[#E9ECEF] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0F172A]" />
        <input data-testid="aff-input-platform" placeholder="Platform" value={form.platform}
          onChange={(e) => setForm({ ...form, platform: e.target.value })}
          className="px-3 py-2 rounded-lg border border-[#E9ECEF] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0F172A]" />
        <input data-testid="aff-input-handle" placeholder="@handle" value={form.handle}
          onChange={(e) => setForm({ ...form, handle: e.target.value })}
          className="px-3 py-2 rounded-lg border border-[#E9ECEF] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0F172A]" />
        <div className="flex gap-2">
          <input data-testid="aff-input-revshare" type="number" min={0} max={100} step={1} title="Revenue share %"
            value={form.rev_share_pct}
            onChange={(e) => setForm({ ...form, rev_share_pct: e.target.value })}
            className="w-16 px-2 py-2 rounded-lg border border-[#E9ECEF] bg-white text-sm font-mono-num text-[#0F172A] focus:outline-none focus:border-[#0F172A]" />
          <button data-testid="aff-submit-btn" onClick={create} disabled={busy || !form.code}
            className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-[#0F172A] text-white text-sm font-semibold hover:bg-[#1E293B] disabled:opacity-50">
            <Plus size={14} /> {busy ? "Creating…" : "Add"}
          </button>
        </div>
      </div>

      {affiliates.length === 0 ? (
        <div className="text-sm text-[#64748B] py-4 text-center">no affiliate codes yet — add your first one above</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-[#64748B]">
              <tr>
                <th className="text-left py-2 pr-3">Code</th>
                <th className="text-left py-2 pr-3">Name</th>
                <th className="text-left py-2 pr-3">Platform</th>
                <th className="text-right py-2 pr-3">Conv.</th>
                <th className="text-right py-2 pr-3">MRR</th>
                <th className="text-right py-2 pr-3">Commission owed</th>
                <th className="text-left py-2 pr-3">Share links</th>
                <th className="text-right py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {affiliates.map((a) => (
                <tr key={a.code} className="border-t border-[#E9ECEF] hover:bg-[#FAFBFC] cursor-pointer" onClick={() => setDrawerCode(a.code)} data-testid={`aff-row-${a.code}`}>
                  <td className="py-2 pr-3 font-mono-num font-semibold text-[#0F172A]">{a.code}</td>
                  <td className="py-2 pr-3 text-[#475569]">{a.name || "—"}</td>
                  <td className="py-2 pr-3 text-[#475569]">{a.platform || "—"} {a.handle ? <span className="text-[#94A3B8]">· {a.handle}</span> : null}</td>
                  <td className="py-2 pr-3 font-mono-num text-right text-[#0F172A]">{a.conversions}</td>
                  <td className="py-2 pr-3 font-mono-num text-right text-[#0F172A]">${(a.mrr_usd || 0).toFixed(2)}</td>
                  <td className="py-2 pr-3 font-mono-num text-right text-[#B45309]">${(a.commission_pending_usd || 0).toFixed(2)}</td>
                  <td className="py-2 pr-3" onClick={(e) => e.stopPropagation()}>
                    <button data-testid={`aff-copy-${a.code}`} onClick={() => copy(link(a.code))}
                      className="text-[#047857] text-xs font-semibold hover:underline">Ref link</button>
                    <span className="text-[#CBD5E1] mx-1">·</span>
                    <button data-testid={`aff-copy-partner-${a.code}`} onClick={() => copy(partnerLink(a.code))}
                      className="text-[#047857] text-xs font-semibold hover:underline">Partner dash</button>
                  </td>
                  <td className="py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    <button data-testid={`aff-archive-${a.code}`} onClick={() => archive(a.code)} disabled={busyRow === a.code}
                      className="inline-flex items-center gap-1 text-[#B91C1C] text-xs font-semibold hover:underline disabled:opacity-50">
                      <Trash2 size={11} /> Archive
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AffiliateDrawer code={drawerCode} onClose={() => setDrawerCode(null)} />
    </Section>
  );
}

export default function AdminPage() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [orphans, setOrphans] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    setBusy(true);
    setErr(null);
    try {
      const [s, o, a, an] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/orphaned-payments`, { withCredentials: true }),
        apiAdminListAffiliates().catch(() => ({ affiliates: [] })),
        apiAdminAnalytics().catch(() => null),
      ]);
      setStats(s.data);
      setOrphans(o.data.orphans || []);
      setAffiliates(a?.affiliates || []);
      setAnalytics(an);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 403) setErr("You aren't on the admin allowlist.");
      else setErr(e?.response?.data?.detail || "Failed to load admin stats");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { if (user) load(); }, [user]);

  const claim = async (payment_id) => {
    const target = window.prompt(`Grant ${payment_id} to which session_id?`, "u:user_");
    if (!target) return;
    try {
      const r = await axios.post(`${API}/admin/orphaned-payments/claim`, { payment_id, target_session_id: target }, { withCredentials: true });
      toast.success(`Granted ${r.data.granted} credits (${r.data.plan})`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Claim failed");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#64748B]"><Loader2 className="animate-spin" size={20} /></div>;

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full p-6 rounded-2xl bg-white border border-[#E9ECEF] text-center">
          <div className="text-lg font-semibold text-[#0F172A] mb-2">Admin sign-in required</div>
          <p className="text-sm text-[#475569] mb-4">This dashboard is restricted to accounts on the admin allowlist.</p>
          <Link to="/" className="inline-block px-5 py-2.5 rounded-full bg-[#0F172A] text-white text-sm font-semibold">Back to app</Link>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full p-6 rounded-2xl bg-white border border-[#E9ECEF] text-center">
          <AlertTriangle className="mx-auto mb-2 text-[#B45309]" size={20} />
          <div className="text-lg font-semibold text-[#0F172A] mb-2">Not authorized</div>
          <p className="text-sm text-[#475569] mb-4">{err}</p>
          <Link to="/" className="text-[#047857] underline text-sm">Back to app</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFBFC] p-4 sm:p-8" data-testid="admin-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-[#0F172A]">Admin</h1>
            <p className="text-sm text-[#64748B]">rsell ai — live ops</p>
          </div>
          <button data-testid="admin-refresh-btn" onClick={load} disabled={busy}
            className="px-4 py-2 rounded-full bg-white border border-[#E9ECEF] text-sm font-semibold text-[#0F172A] hover:border-[#0F172A] transition-colors disabled:opacity-50">
            {busy ? "Loading…" : "Refresh"}
          </button>
        </div>

        {!stats ? (
          <div className="text-center py-16 text-[#64748B]"><Loader2 className="animate-spin inline mr-2" size={16} /> loading stats…</div>
        ) : (
          <>
            {/* Alerts first — surface issues before scrolling */}
            <AlertsBanner alerts={analytics?.alerts || []} />

            {/* Core snapshot */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
              <Card label="Total users" value={stats.total_users.toLocaleString()} icon={Users} testid="stat-total-users" />
              <Card label="Paying subs" value={stats.total_paying.toLocaleString()} icon={CreditCard} tone="good" hint={`${stats.conversion_pct}% conversion`} testid="stat-total-paying" />
              <Card label="Scans today" value={stats.scans_today.toLocaleString()} icon={ScanLine} testid="stat-scans-today" />
              <Card label="Revenue this month" value={`$${stats.revenue_month_usd.toFixed(2)}`} icon={DollarSign} tone="good" testid="stat-revenue-month" />
            </div>

            {/* Funnel */}
            {analytics && (
              <Section title="Funnel" subtitle="Signup → activation (first scan) → paid conversion" testid="admin-funnel">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <Card label="Signups (all-time)" value={analytics.funnel.signups.toLocaleString()} testid="funnel-signups" />
                  <Card label="Signups this mo." value={analytics.funnel.signups_this_month.toLocaleString()} testid="funnel-signups-month" />
                  <Card label="Activated" value={analytics.funnel.activated_users.toLocaleString()} hint={`${analytics.funnel.activation_rate_pct}% of signups`} tone="good" testid="funnel-activated" />
                  <Card label="Paid" value={analytics.funnel.paying_sessions.toLocaleString()} hint={`${analytics.funnel.conversion_pct}% overall`} tone="good" testid="funnel-paid" />
                  <Card label="TTP median" value={analytics.funnel.time_to_first_payment_hours_median != null ? `${analytics.funnel.time_to_first_payment_hours_median}h` : "—"} icon={TrendingUp} hint="Signup → first payment" testid="funnel-ttp" />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <Card label="Referred conversions" value={analytics.funnel.referred_conversions.toLocaleString()} hint="Came via an affiliate code" tone="good" testid="funnel-referred" />
                  <Card label="Organic conversions" value={analytics.funnel.non_referred_conversions.toLocaleString()} hint="No affiliate attribution" testid="funnel-organic" />
                </div>
              </Section>
            )}

            {/* Retention */}
            {analytics && (
              <Section title="Retention" subtitle="Subscribers by tier and churn %" testid="admin-retention">
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <Card label="Thrifter (mo.)" value={analytics.retention.subs_this_month_by_tier.thrifter} hint={`Last mo: ${analytics.retention.subs_last_month_by_tier.thrifter}`} testid="ret-thrifter" />
                  <Card label="Reseller (mo.)" value={analytics.retention.subs_this_month_by_tier.reseller} hint={`Last mo: ${analytics.retention.subs_last_month_by_tier.reseller}`} tone="good" testid="ret-reseller" />
                  <Card label="Pro (mo.)" value={analytics.retention.subs_this_month_by_tier.pro} hint={`Last mo: ${analytics.retention.subs_last_month_by_tier.pro}`} tone="good" testid="ret-pro" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Card label="Churned" value={analytics.retention.churned_users} hint={`of ${analytics.retention.last_month_payer_count} last-month payers`} tone={analytics.retention.churn_pct > 10 ? "warn" : "default"} testid="ret-churned" />
                  <Card label="Churn %" value={`${analytics.retention.churn_pct}%`} icon={Percent} tone={analytics.retention.churn_pct > 10 ? "warn" : "good"} testid="ret-churn-pct" />
                </div>
              </Section>
            )}

            {/* Scan health */}
            {analytics && (
              <Section title="Scan health" subtitle="Accuracy proxy + cache/stage-1 efficiency" testid="admin-scanhealth">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                  <Card label="Scans today" value={analytics.scan_health.scans_today.toLocaleString()} icon={ScanLine} testid="sh-today" />
                  <Card label="This week" value={analytics.scan_health.scans_week.toLocaleString()} testid="sh-week" />
                  <Card label="This month" value={analytics.scan_health.scans_month.toLocaleString()} testid="sh-month" />
                  <Card label="Failed / low-conf" value={`${analytics.scan_health.fail_rate_pct}%`} icon={Activity} tone={analytics.scan_health.fail_rate_pct > 20 ? "warn" : "good"} hint={`${analytics.scan_health.failed_or_low_conf_month} events this mo.`} testid="sh-fail-rate" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card label="Cache hit rate" value={`${analytics.scan_health.cache_hit_rate_pct}%`} icon={Zap} tone="good" hint="24h exact-match cache" testid="sh-cache" />
                  <Card label="Stage-1 hit rate" value={`${analytics.scan_health.stage1_hit_rate_pct}%`} tone="good" hint="Tag-only identification" testid="sh-stage1" />
                  <Card label="Studio uses (Reseller)" value={analytics.scan_health.photo_studio_by_tier_month.reseller} icon={Camera} testid="sh-studio-reseller" />
                  <Card label="Studio uses (Pro)" value={analytics.scan_health.photo_studio_by_tier_month.pro} icon={Camera} tone="good" testid="sh-studio-pro" />
                </div>
              </Section>
            )}

            {/* Cost */}
            {analytics && (
              <Section title="Credit cost (estimated)" subtitle={analytics.cost.note} testid="admin-cost">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                  <Card label="Credits today" value={analytics.cost.credits_today_est.toLocaleString()} icon={Zap} testid="cost-today" />
                  <Card label="Credits this mo." value={analytics.cost.credits_month_est.toLocaleString()} icon={Zap} testid="cost-month" />
                  <Card label="7-day avg / day" value={analytics.cost.avg_7d_credits_per_day.toLocaleString()} testid="cost-avg7" />
                  <Card label="Credits / paying user" value={analytics.cost.credits_per_paying_user_est.toLocaleString()} tone="good" testid="cost-per-paying" />
                </div>
                <div className="p-3 rounded-xl bg-[#FEF3C7] border border-[#F59E0B]/40 text-[12px] text-[#78350F] leading-relaxed">
                  <strong>Estimation note:</strong> the Universal Key doesn&apos;t expose a live balance via public API. These figures use measured per-call pricing (~2.5c/scan, ~4c/photo-studio). Swap to real balance the moment Emergent exposes it.
                </div>
              </Section>
            )}

            {/* Existing tier snapshot */}
            <Section title="Subscription tiers snapshot" subtitle="Current active-subscription counts" testid="admin-tier-snapshot">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card label="Thrifter" value={stats.paying_by_tier.thrifter.toLocaleString()} hint="$5.99/mo · 15 scans" testid="stat-tier-thrifter" />
                <Card label="Reseller" value={stats.paying_by_tier.reseller.toLocaleString()} hint="$14.99/mo · 60 scans" tone="good" testid="stat-tier-reseller" />
                <Card label="Pro" value={stats.paying_by_tier.pro.toLocaleString()} hint="$29.99/mo · 125 scans" tone="good" testid="stat-tier-pro" />
                <Card label="Orphaned payments" value={stats.orphan_count.toLocaleString()} icon={AlertTriangle} tone={stats.orphan_count ? "warn" : "default"} hint="unclaimed Whop paid" testid="stat-orphans" />
              </div>
            </Section>

            {/* Orphaned payments */}
            <Section title="Orphaned payments" subtitle="Unclaimed Whop payments — search support tickets by email here" testid="admin-orphans-section">
              {orphans.length === 0 ? (
                <div className="text-sm text-[#64748B] py-4 text-center">no orphans right now — every paid Whop payment is granted</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-widest text-[#64748B]">
                      <tr>
                        <th className="text-left py-2 pr-3">Buyer email</th>
                        <th className="text-left py-2 pr-3">Checkout email</th>
                        <th className="text-left py-2 pr-3">Payment</th>
                        <th className="text-right py-2 pr-3">Amount</th>
                        <th className="text-left py-2 pr-3">Paid at</th>
                        <th className="text-right py-2">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orphans.map((o) => (
                        <tr key={o.payment_id} className="border-t border-[#E9ECEF]">
                          <td className="py-2 pr-3 font-medium text-[#0F172A]">{o.buyer_email || "—"}</td>
                          <td className="py-2 pr-3 text-[#475569]">{o.checkout_email || "—"}</td>
                          <td className="py-2 pr-3 font-mono-num text-xs text-[#475569]">{o.payment_id}</td>
                          <td className="py-2 pr-3 font-mono-num text-right text-[#0F172A]">${(o.amount || 0).toFixed?.(2) ?? o.amount}</td>
                          <td className="py-2 pr-3 text-xs text-[#64748B]">{o.paid_at ? new Date(o.paid_at).toLocaleString() : "—"}</td>
                          <td className="py-2 text-right">
                            <button data-testid={`orphan-claim-${o.payment_id}`} onClick={() => claim(o.payment_id)}
                              className="inline-flex items-center gap-1 text-[#047857] text-xs font-semibold hover:underline">
                              Grant <ArrowRight size={11} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>

            <AffiliatePanel affiliates={affiliates} onRefresh={load} />

            <div className="text-[11px] text-[#94A3B8] mt-6 text-center">stats generated {new Date(stats.generated_at).toLocaleString()}</div>
          </>
        )}
      </div>
    </div>
  );
}
